/**
 * Dictation clips that could not be transcribed are never thrown away: the last few stay in memory
 * AND in IndexedDB (this viewer's origin), so a reload, a closed tab or a dead network still leaves
 * the radiologist a "Reintentar" and a "Descargar audio". ris stores nothing for the Asistente's
 * transcription (it is a pass-through), so this list is the only copy of the audio.
 */

/** How many failed clips are kept; the oldest goes when a new one arrives past this. */
export const MAX_KEPT_CLIPS = 5;

/** Why a clip was kept, for the label in the list. */
export type KeptReason = 'failed' | 'too_large' | 'interrupted';

export interface KeptClip {
  id: string;
  /** The study whose transcription route the clip goes to on retry. */
  study: string;
  audio: Blob;
  /** Epoch ms when it was recorded. */
  createdAt: number;
  reason: KeptReason;
}

/** Where kept clips persist. IndexedDB in the browser; a fake in the unit spec. */
export interface KeptClipBackend {
  load(): Promise<KeptClip[]>;
  put(clip: KeptClip): Promise<void>;
  remove(id: string): Promise<void>;
}

const DB_NAME = 'ris-viewer-dictation';
const DB_VERSION = 1;
const STORE = 'kept_clips';

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** IndexedDB on the viewer's origin. Every method rejects when IndexedDB is unavailable. */
export class IndexedDbClipBackend implements KeptClipBackend {
  private db: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    if (!this.db) {
      if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB unavailable'));
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'id' });
      };
      this.db = requestResult(request);
      // A failed open is retried on the next call instead of failing forever.
      this.db.catch(() => { this.db = null; });
    }
    return this.db;
  }

  async load(): Promise<KeptClip[]> {
    const db = await this.open();
    const rows = await requestResult(db.transaction(STORE, 'readonly').objectStore(STORE).getAll());
    return (rows as KeptClip[]).filter((row) => row && row.audio instanceof Blob);
  }

  async put(clip: KeptClip): Promise<void> {
    const tx = (await this.open()).transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(clip);
    await transactionDone(tx);
  }

  async remove(id: string): Promise<void> {
    const tx = (await this.open()).transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    await transactionDone(tx);
  }
}

let sequence = 0;
function newId(): string {
  sequence += 1;
  return `${Date.now().toString(36)}-${sequence}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The kept list: memory is the truth for the UI (it works even when IndexedDB is blocked), every
 * change is mirrored to the backend, and `ready` merges what an earlier page left there.
 */
export class KeptClipStore {
  private clips: KeptClip[] = [];
  private readonly listeners = new Set<() => void>();
  readonly ready: Promise<void>;

  constructor(private readonly backend: KeptClipBackend, private readonly max = MAX_KEPT_CLIPS) {
    this.ready = this.restore();
  }

  /** Newest first. */
  list(): KeptClip[] {
    return [...this.clips];
  }

  get(id: string): KeptClip | undefined {
    return this.clips.find((c) => c.id === id);
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Keeps a clip (memory first, then IndexedDB) and drops the oldest past the limit. */
  async keep(study: string, audio: Blob, reason: KeptReason): Promise<KeptClip> {
    const clip: KeptClip = { id: newId(), study, audio, createdAt: Date.now(), reason };
    this.clips = [clip, ...this.clips];
    const evicted = this.clips.slice(this.max);
    this.clips = this.clips.slice(0, this.max);
    this.changed();
    await this.persist(() => this.backend.put(clip));
    for (const old of evicted) await this.persist(() => this.backend.remove(old.id));
    return clip;
  }

  async remove(id: string): Promise<void> {
    this.clips = this.clips.filter((c) => c.id !== id);
    this.changed();
    await this.persist(() => this.backend.remove(id));
  }

  private async restore(): Promise<void> {
    let stored: KeptClip[] = [];
    try {
      stored = await this.backend.load();
    } catch (error) {
      console.warn('[viewer] kept dictation clips could not be read', error);
      return;
    }
    const known = new Set(this.clips.map((c) => c.id));
    const merged = [...this.clips, ...stored.filter((c) => !known.has(c.id))].sort((a, b) => b.createdAt - a.createdAt);
    const evicted = merged.slice(this.max);
    this.clips = merged.slice(0, this.max);
    this.changed();
    for (const old of evicted) await this.persist(() => this.backend.remove(old.id));
  }

  /** A storage failure keeps the clip in memory and says so in the console, never throws. */
  private async persist(write: () => Promise<void>): Promise<void> {
    try {
      await write();
    } catch (error) {
      console.warn('[viewer] kept dictation clip not saved to IndexedDB (kept in memory)', error);
    }
  }

  private changed(): void {
    this.listeners.forEach((listener) => listener());
  }
}

/** File name for "Descargar audio": `dictado-<study tail>-<yyyymmdd-hhmmss>.<ext>`. */
export function keptClipFileName(clip: KeptClip): string {
  const d = new Date(clip.createdAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  const ext = clip.audio.type.includes('mp4') ? 'm4a' : clip.audio.type.includes('ogg') ? 'ogg' : 'webm';
  const tail = clip.study.replace(/[^0-9A-Za-z.]/g, '').slice(-12) || 'estudio';
  return `dictado-${tail}-${stamp}.${ext}`;
}
